import type { TelemetryPointResponse } from 'generatedTypes/flight/models/telemetryPointResponse';

import { distance } from '@turf/turf';

const SQUARE_METERS_PER_HECTARE = 10000;
const MILLISECONDS_PER_MINUTE = 60_000;

type FlightTotals = {
    totalAreaHectares: number;
    totalVolumeAppliedLiters: number;
};

type ComputeFlightTotalsArgs = {
    fallbackSwathMeters: number;
    points: TelemetryPointResponse[];
};

export function computeFlightTotalsFromTelemetry({
    fallbackSwathMeters,
    points
}: ComputeFlightTotalsArgs): FlightTotals {
    if (points.length < 2) {
        return { totalAreaHectares: 0, totalVolumeAppliedLiters: 0 };
    }

    let areaSquareMeters = 0;
    let volumeLiters = 0;

    for (let i = 0; i < points.length - 1; i++) {
        const start = points[i];
        const end = points[i + 1];

        if (!start.isPumpOn || !end.isPumpOn) {
            continue;
        }

        const swathMeters = start.swathWidthM > 0 ? start.swathWidthM : fallbackSwathMeters;

        if (swathMeters <= 0) {
            continue;
        }

        const distanceMeters = distance(
            [start.longitude, start.latitude],
            [end.longitude, end.latitude],
            { units: 'meters' }
        );

        if (Number.isFinite(distanceMeters) && distanceMeters > 0) {
            areaSquareMeters += distanceMeters * swathMeters;
        }

        const deltaMilliseconds =
            new Date(end.recordedAt).getTime() - new Date(start.recordedAt).getTime();
        const deltaMinutes = deltaMilliseconds / MILLISECONDS_PER_MINUTE;

        if (Number.isFinite(deltaMinutes) && deltaMinutes > 0) {
            const averageFlowLpm =
                (start.flowRateLitersPerMinute + end.flowRateLitersPerMinute) / 2;

            if (averageFlowLpm > 0) {
                volumeLiters += averageFlowLpm * deltaMinutes;
            }
        }
    }

    return {
        totalAreaHectares: areaSquareMeters / SQUARE_METERS_PER_HECTARE,
        totalVolumeAppliedLiters: volumeLiters
    };
}
